import { Tooltip } from "antd";
import momentTimeZone from "moment-timezone";
import { useContext, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/styles.css";
import AppContext from "../../../../contexts/AppContext";
import LayoutContext from "../../../../contexts/LayoutContext";
import UserContext from "../../../../contexts/UserContext";
import { AddToCartDataFormatter, transformData } from "../../../../utils/AddToCartDataFormatter";
import * as Formatter from "../../../../utils/Formatter";
import { getMobileOperatingSystem } from "../../../../utils/helperFunctions";
import * as Library from "../../../../utils/Library";

export default function BOTagBodyReadyToBuy({
    deliveryData,
    deliveryMethod,
    GTINbarcode,
    selectedQuantity,
    selectedColor,
    selectedSize,
    deliveryPreference,
    animate,
    setAnimate,
    fireworks,
    setFireworks
}) {
    /* #region VARS */
    const { setCheckoutCartData, userData, checkoutCartData, shippingRecipient, setQuickBuyData, quickBuyData } = useContext(UserContext);
    const {
        selectedBotag,
        primaryImageData,
        shopperBagID,
        setShopperBagItem,
        setShopperBagID,
        shopperBagItem,
        setBotagToPurchaseData,
        botagToPurchaseData
    } = useContext(AppContext);
    const { setShowRightSidebar, setShowStoreInformation, setStoreInformation, setQuickBuyPopup, setShowFireWorks } =
        useContext(LayoutContext);

    const userGPSCoordinates = JSON.parse(localStorage.getItem("userCoords"))?.coords;
    const userAddress = JSON.parse(localStorage.getItem("userLocation"))?.location;
    //const isStoreOpen = true;
    const storeDetails = deliveryData?.storeDetail;
    const currencySymbol = "kr";

    const distanceToStore = Formatter.formatDistance(deliveryData?.distanceToStore, "km", "--");
    const storeName = deliveryData?.storeDetail?.name || "--";
    const price = deliveryData?.price;
    const discountPrice = deliveryData?.discountPrice;
    const priceStr = Formatter.formatPrice(price, "");
    const discountPriceStr = discountPrice ? Formatter.formatPrice(discountPrice, "") : "";
    const deliveryCharges = deliveryData?.deliveryCharges;
    const deliveryChargesStr = Formatter.formatPrice(deliveryCharges, "");
    const totalPrice = deliveryData?.totalPrice ? deliveryData?.totalPrice : deliveryData?.price;
    const totalPriceStr = Formatter.formatPrice(price * selectedQuantity + (deliveryCharges || 0), "");
    const totalDiscountPriceStr = Formatter.formatPrice(discountPrice * selectedQuantity + (deliveryCharges || 0), "");
    const operatingSystem = getMobileOperatingSystem();

    /* #endregion */

    /* #Region EFFECTS */
    useEffect(() => {
        setBotagToPurchaseData({
            GTINbarcode: GTINbarcode,
            brandInfo: {
                _id: selectedBotag.labelBrandID,
                name: selectedBotag.labelBrandName
            },
            productInfo: {
                _id: selectedBotag.data.productObjID,
                name: selectedBotag.productName,
                productType: selectedBotag.productType
            },
            selectedSize: selectedSize,
            selectedColor: selectedColor,
            selectedQuantity: selectedQuantity,
            deliveryPreference: deliveryPreference,
            deliveryData
        });

        return () => {
            setBotagToPurchaseData(undefined);
        };
    }, [deliveryData]);
    /* #endregion */

    /* #region METHODS */
    const isStoreOpen = () => {
        const dayOfWeek = new Date().toLocaleString("en-us", { weekday: "long" }).toLowerCase();
        const openHours = storeDetails?.openHours?.[dayOfWeek];

        if (!openHours || openHours.closedAllDay) return false;
        if (openHours.openAllDay) return true;

        const currentTime = momentTimeZone.tz("Europe/Oslo").format("HH:mm");
        const hourStart = openHours?.hourStart; // "10:00"
        const hourEnd = openHours?.hourEnd; // "19:00"
        return currentTime >= hourStart && currentTime <= hourEnd;
    };

    const getRandomColor = () => {
        const colors = [
            "rgba(255,0,0,1)", // Red
            "rgba(0,255,0,1)", // Green
            "rgba(0,0,255,1)", // Blue
            "rgba(255,255,0,1)", // Yellow
            "rgba(255,105,180,1)" // Pink
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const getUserShopperBag = BOthebotAccountID => {
        Library.makePostRequest("getShopperBagsData", { BOthebotAccountID: BOthebotAccountID })
            .then(res => {
                const { data } = res;
                const shopperBags = data.data;
                shopperBags.forEach(bag => {
                    setShopperBagID(bag._id);
                    setShopperBagItem(bag.items);
                });
            })
            .catch(error => {
                console.error("Error fetching shopper bags data:", error);
            });
    };

    const onAddToCheckoutClick = async () => {
        setAnimate(true);
        // Fireworks animation
        for (let i = 0; i < 15; i++) {
            const firework = {
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                color: getRandomColor(),
                size: Math.random() * 30 + 10
            };
            setFireworks(prevFireworks => [...prevFireworks, firework]);
        }

        setTimeout(() => {
            setShowFireWorks(true);
            setTimeout(() => setShowFireWorks(false), 500);
        }, 2000);

        const item = document.getElementById("item");
        item?.addEventListener("animationend", () => setAnimate(false), { once: true });
        // Prepare BOTagData
        const BOTagData = { ...selectedBotag };
        Object.assign(BOTagData, {
            logoURL: primaryImageData[0]?.brandLogo,
            GTINbarcode,
            selectedQuantity,
            selectedColor,
            selectedSize,
            deliveryPreference,
            deliveryMethod,
            storeName,
            storeDetails,
            recipient:
                shippingRecipient?.firstName && shippingRecipient?.lastName
                    ? `${shippingRecipient.firstName} ${shippingRecipient.lastName}`
                    : userData?.firstName && userData?.lastName
                    ? `${userData.firstName} ${userData.lastName}`
                    : "",
            recipientJson: {
                firstName: shippingRecipient?.firstName || userData?.firstName,
                lastName: shippingRecipient?.lastName || userData?.lastName
            },
            selectedColorName:
                BOTagData?.colors?.find(color => Formatter.formatAndCompareColors(color?.colorCode, BOTagData?.selectedColor))?.color ||
                "--",
            totalStock: BOTagData.stockData.find(
                stock =>
                    stock.size === BOTagData?.selectedSize && Formatter.formatAndCompareColors(stock.colorCode, BOTagData?.selectedColor)
            )?.totalStock
        });

        // Remove unnecessary BOTagData fields
        ["data", "sizes", "colors", "naturalX", "naturalY", "x", "y", "number", "selected", "stockData", "category", "completed"].forEach(
            field => delete BOTagData[field]
        );

        const BOTagAlgorithmData = { ...deliveryData };
        const FormattedData = {
            distanceToStore,
            price: discountPrice || price,
            priceStr: Formatter.formatPrice(discountPrice || price),
            deliveryCharges,
            deliveryChargesStr,
            totalPrice: (discountPrice || price) * selectedQuantity + (deliveryCharges || 0),
            totalPriceStr: Formatter.formatPrice((discountPrice || price) * selectedQuantity + (deliveryCharges || 0))
        };
        let updatedCartData = { ...checkoutCartData }; // Clone current cart data
        const itemID = uuidv4();
        if (deliveryMethod === "courier") {
            const alreadyExistsCourier = checkoutCartData.items.find(
                item => item.BOTagData.storeName === storeName && item.BOTagData.deliveryMethod === "courier"
            );
            if (alreadyExistsCourier) {
                FormattedData.deliveryCharges = 0;
                FormattedData.deliveryChargesStr = "";
                FormattedData.totalPrice = price * selectedQuantity;
                FormattedData.totalPriceStr = Formatter.formatPrice(price * selectedQuantity);
                BOTagAlgorithmData.deliveryCharges = 0;
            }
        }

        const newItem = {
            itemID,
            BOTagData,
            BOTagAlgorithmData,
            FormattedData,
            UserData: userData,
            UserAddress: userAddress,
            UserGPSCoordinates: userGPSCoordinates
        };

        updatedCartData = {
            ...checkoutCartData,
            //items: [newItem, ...checkoutCartData.items],
            items: [newItem],
            total: checkoutCartData.total + FormattedData.totalPrice
        };
        //}
        // Call createShopperBag API with updated cart data
        try {
            const formattedData = AddToCartDataFormatter(updatedCartData, localStorage.getItem("pid"));
            const shopperLocalBag = localStorage.getItem("shopperBag");
            if (!localStorage.getItem("pid")) {
                if (shopperLocalBag) {
                    // Parse existing shopperBag and append new items
                    const existingBag = JSON.parse(shopperLocalBag);
                    if (formattedData.shopperBag?.items.length) {
                        existingBag.shopperBag.items = [...existingBag.shopperBag.items, ...formattedData.shopperBag.items];
                        localStorage.setItem("shopperBag", JSON.stringify(existingBag));
                        setShopperBagItem(existingBag.shopperBag.items);
                    } else {
                        console.error("No items to append in formattedData:", formattedData);
                    }
                } else {
                    localStorage.setItem("shopperBag", JSON.stringify(formattedData));
                    setShopperBagItem(formattedData.shopperBag.items);
                }
            } else {
                if (!formattedData || Object.keys(formattedData).length === 0) {
                    console.error("Formatted data is empty:", formattedData);
                }
                if (shopperLocalBag) {
                    const existingBag = JSON.parse(shopperLocalBag);
                    if (formattedData.shopperBag?.items) {
                        existingBag.shopperBag.items = [...existingBag.shopperBag.items, ...formattedData.shopperBag.items];
                        formattedData.shopperBag = existingBag.shopperBag; // Update formattedData with merged items
                    }
                } else {
                    formattedData.shopperBag = {
                        items: [...shopperBagItem, ...formattedData.shopperBag.items],
                        name: "bag"
                    };
                }

                const payload = shopperBagID
                    ? {
                          shopperBagID: shopperBagID,
                          shopperBag: formattedData.shopperBag
                      }
                    : formattedData;
                payload.shopperBag.items.forEach(item => {
                    delete item.brandDetails;
                    delete item.productDetails;
                });
                payload.shopperBag.items = transformData(payload.shopperBag.items);
                Library.makePostRequest(shopperBagID ? "updateShopperBag" : "createShopperBag", payload, false).then(res => {
                    const { data } = res;
                    if (data?.status === 200 && typeof data?.message === "string") {
                        getUserShopperBag(localStorage.getItem("pid"));
                        localStorage.removeItem("shopperBag");
                    }
                });
            }
        } catch (error) {
            console.error("Failed to create shopper bag:", error);
            // Optionally notify user
        }

        setShowRightSidebar(true);
    };
    const onQuickBuyClick = () => {
        const BOTagData = { ...selectedBotag };

        BOTagData.GTINbarcode = GTINbarcode;
        BOTagData.selectedQuantity = selectedQuantity;
        BOTagData.selectedColor = selectedColor;
        BOTagData.selectedSize = selectedSize;
        BOTagData.deliveryPreference = deliveryPreference;
        BOTagData.deliveryMethod = deliveryMethod;
        BOTagData.storeName = storeName;
        BOTagData.storeDetails = storeDetails;
        BOTagData.recipient =
            shippingRecipient?.firstName && shippingRecipient?.lastName
                ? shippingRecipient?.firstName + " " + shippingRecipient?.lastName
                : userData?.firstName && userData?.lastName
                ? userData?.firstName + " " + userData?.lastName
                : "";
        BOTagData.recipientJson = {};
        BOTagData.recipientJson.firstName = shippingRecipient?.firstName ? shippingRecipient?.firstName : userData?.firstName;
        BOTagData.recipientJson.lastName = shippingRecipient?.lastName ? shippingRecipient?.lastName : userData?.lastName;
        BOTagData.selectedColorName =
            BOTagData?.colors?.find(color => Formatter.formatAndCompareColors(color?.colorCode, BOTagData?.selectedColor))?.color || "--";

        BOTagData.totalStock = BOTagData.stockData.find(
            stock => stock.size === BOTagData?.selectedSize && Formatter.formatAndCompareColors(stock.colorCode, BOTagData?.selectedColor)
        )?.totalStock;

        /* #region REMOVING UNNECESSARY DATA FROM BOTag */
        delete BOTagData.data;
        delete BOTagData.sizes;
        delete BOTagData.colors;
        //delete BOTagData.images;
        delete BOTagData.naturalX;
        delete BOTagData.naturalY;
        delete BOTagData.x;
        delete BOTagData.y;
        delete BOTagData.number;
        delete BOTagData.selected;
        delete BOTagData.stockData;
        delete BOTagData.category;
        delete BOTagData.completed;
        /* #endregion */

        const BOTagAlgorithmData = { ...deliveryData };
        const FormattedData = {
            distanceToStore,
            price: discountPrice ? discountPrice : price,
            priceStr: Formatter.formatPrice(discountPrice ? discountPrice : price),
            deliveryCharges,
            deliveryChargesStr: deliveryChargesStr,
            totalPrice: (discountPrice ? discountPrice : price) * selectedQuantity + (deliveryCharges || 0), // add deliveryCharges if any
            totalPriceStr: Formatter.formatPrice((discountPrice ? discountPrice : price) * selectedQuantity + (deliveryCharges || 0))
        };

        const itemID = uuidv4(); //USE THIS AS A UNIQUE ID FOR EACH ITEM IN THE CART

        const item = {
            itemID,
            BOTagData,
            BOTagAlgorithmData,
            FormattedData,
            UserData: userData,
            UserAddress: userAddress,
            UserGPSCoordinates: userGPSCoordinates
        };
        setQuickBuyData(prevState => {
            return {
                ...prevState,
                items: [item],
                total: FormattedData.totalPrice
            };
        });
        setQuickBuyPopup(true);
    };
    const showStoreinfoFunc = () => {
        setShowStoreInformation(true);
        setStoreInformation(storeDetails);
    };

    /* #endregion */

    return (
        <div className="frame-313502" style={{ gap: operatingSystem === "iOS" && "10px" }}>
            <div className="frame-313500">
                <div className="frame-313499">
                    <div className="frame-313323">
                        <div className="open-234-m-from-you">
                            <span className={`open-234-m-from-you-span ${isStoreOpen() ? "open" : "closed"}`}>
                                {isStoreOpen() ? "Open" : "Closed"}
                            </span>

                            {deliveryMethod === "pickup" ? (
                                <span className="open-234-m-from-you-span2">
                                    {isStoreOpen() ? ` - Store is ${distanceToStore} from you` : " - You can still buy now"}
                                </span>
                            ) : deliveryMethod === "courier" ? (
                                <span className="open-234-m-from-you-span2">
                                    {` - Delivery ~ ${deliveryData?.estimatedTime || "--"} `}
                                    {isStoreOpen() ? "" : "after opening"}
                                </span>
                            ) : deliveryMethod === "shipping" ? (
                                <span
                                    style={{
                                        position: "relative",
                                        verticalAlign: "text-top",
                                        display: "inline-flex",
                                        gap: "4px"
                                    }}>
                                    <span className="open-234-m-from-you-span2" style={{ position: "relative", verticalAlign: "text-top" }}>
                                        &nbsp;{`- ${deliveryData?.estimatedTime || "--"} with `}
                                    </span>
                                    <img
                                        className="vector"
                                        alt="wolt"
                                        src={deliveryData?.cf?.logoURL}
                                        style={{ height: "10px", marginTop: "2px" }}
                                    />
                                </span>
                            ) : (
                                <span>Unknown delivery method: {deliveryMethod}</span>
                            )}
                        </div>
                    </div>
                    <div className="frame-313324">
                        <Tooltip
                            placement="top"
                            title={storeName.length > 20 ? storeName : ""}
                            color="#212121"
                            overlayClassName="custom-tool-tip">
                            <div className="sams-e-sams-e-akersgata" onClick={showStoreinfoFunc}>
                                {storeName.length > 20 ? `${storeName.substring(0, 20)}...` : storeName}
                            </div>
                        </Tooltip>
                    </div>
                </div>
                <div className="frame-313327">
                    <div className="frame-313325">
                        <div className="line-26"></div>
                        <div className="frame-313319">
                            <div className="item-1-500-nok">
                                <span>
                                    {deliveryMethod === "pickup" ? (
                                        <span className="item-1-500-nok-span">
                                            Item{" "}
                                            {discountPriceStr ? (
                                                <>
                                                    <span
                                                        style={{
                                                            textDecoration: "line-through"
                                                        }}>
                                                        {priceStr}
                                                    </span>{" "}
                                                    <span
                                                        style={{
                                                            color: "#FF0000"
                                                        }}>
                                                        {discountPriceStr}
                                                    </span>
                                                </>
                                            ) : (
                                                <span>{priceStr}</span>
                                            )}{" "}
                                            <span
                                                style={
                                                    discountPriceStr
                                                        ? {
                                                              color: "#FF0000"
                                                          }
                                                        : {}
                                                }>
                                                {currencySymbol}
                                            </span>{" "}
                                            {selectedQuantity > 1 ? "(" + selectedQuantity + ")" : ""}
                                        </span>
                                    ) : deliveryCharges !== 0 ? (
                                        <span className="item-1-500-nok-span">
                                            Item{" "}
                                            {discountPriceStr ? (
                                                <>
                                                    <span
                                                        style={{
                                                            textDecoration: "line-through"
                                                        }}>
                                                        {priceStr}
                                                    </span>{" "}
                                                    <span
                                                        style={{
                                                            color: "#FF0000"
                                                        }}>
                                                        {discountPriceStr}
                                                    </span>
                                                </>
                                            ) : (
                                                <span>{priceStr}</span>
                                            )}{" "}
                                            <span
                                                style={
                                                    discountPriceStr
                                                        ? {
                                                              color: "#FF0000"
                                                          }
                                                        : {}
                                                }>
                                                {currencySymbol}
                                            </span>{" "}
                                            {selectedQuantity > 1 ? "(" + selectedQuantity + ")" : ""} / Delivery {deliveryChargesStr}{" "}
                                            {currencySymbol}
                                        </span>
                                    ) : (
                                        <span className="item-1-500-nok-span">
                                            Item {priceStr} {currencySymbol} {selectedQuantity > 1 ? "(" + selectedQuantity + ")" : ""}
                                            <span
                                                style={{
                                                    fontSize: "10px",
                                                    fontWeight: "400",
                                                    color: "rgb(247, 102, 95)"
                                                }}>
                                                / Delivery FREE today!
                                            </span>
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className="line-25"></div>
                    </div>
                    <div className="_1-500-nok">
                        <span>
                            <span className="_1-500-nok-span">
                                {discountPriceStr ? totalDiscountPriceStr : totalPriceStr} {currencySymbol}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
            <div className="frame-3135012">
                <div className="frame-313503" onClick={onAddToCheckoutClick}>
                    <div className="btn-fw-botag" style={{ background: "#212121" }}>
                        <div className="frame-313497">
                            <div className="frame-313349">
                                <div className="button">Add to bag</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="frame-313503" onClick={onQuickBuyClick}>
                    <div className="btn-fw-botag">
                        <div className="frame-313497">
                            <div className="frame-313349">
                                <div className="button">Quick buy</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
